import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SendIcon from '@mui/icons-material/Send'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CustomRadio } from '../../../../../../shared-components/checkbox/CustomRadio'
import AutocompleteCountries from '../../../../../../shared-components/textfield/AutocompleteCountries'
import instance from '../../../../../../../providers/axios.config'
import { typeResponse } from '../../../../../../../utils/types'
import Notiflix from 'notiflix'
import styles from './TaxFormWizard.module.scss'

interface AvailableType {
  code: string
  label: string
  description: string
  is_entity: boolean
}

interface TaxFormWizardProps {
  recommendedType: string | null
  availableTypes: AvailableType[]
  onComplete: () => void
  onCancel: () => void
}

interface CountryValue {
  label: string
  value: string | number
}

const STEP_LABELS = ['select_form_type', 'fill_form_details', 'review_and_sign']

/**
 * TaxFormWizard — multi-step form wizard for IRS tax form submission.
 *
 * Field names and payload shape are aligned 1:1 with the backend
 * FormRequest validation rules (StoreW9TaxFormRequest,
 * StoreW8BenTaxFormRequest, StoreW8BenETaxFormRequest).
 *
 * Notable contract points:
 *   - `is_us_person` is auto-populated based on the selected form type
 *     (true for W-9, false for W-8BEN/W-8BEN-E) and sent on every submit.
 *   - Country fields store only the integer FK (`*_country_id`); the
 *     backend mapper resolves the name at PDF generation time.
 *   - `capacity_to_sign` is a required boolean `accepted` rule on the
 *     backend, so the certification checkbox submits as `true`.
 *   - `signature_date` defaults to today; the user never types it.
 */
export const TaxFormWizard: React.FC<TaxFormWizardProps> = ({
  recommendedType,
  availableTypes,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation()
  const [activeStep, setActiveStep] = useState(0)
  const [selectedType, setSelectedType] = useState(recommendedType ?? 'w8ben')
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [prefillLoaded, setPrefillLoaded] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isDeviatingFromRecommendation = useMemo(
    () => recommendedType !== null && selectedType !== recommendedType,
    [recommendedType, selectedType]
  )

  /**
   * Load prefill data when the seller enters step 1 for a given type.
   */
  const loadPrefill = useCallback(async (type: string) => {
    try {
      const resp = await instance.get(
        `seller/tax-center/tax-forms/prefill/${type}`
      ) as typeResponse

      if ((resp as any).status === 'success' && (resp as any).data?.prefill) {
        setFormData((resp as any).data.prefill)
      }
    } catch (error) {
      console.error('TaxFormWizard: prefill failed', error)
    } finally {
      setPrefillLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (activeStep === 1 && !prefillLoaded) {
      loadPrefill(selectedType)
    }
  }, [activeStep, prefillLoaded, selectedType, loadPrefill])

  /**
   * Update a flat or nested field in formData. Clears matching error.
   */
  const setField = (key: string, value: any) => {
    setFormData((prev) => {
      const keys = key.split('.')
      if (keys.length === 1) {
        return { ...prev, [key]: value }
      }
      const [parent, child] = keys
      return {
        ...prev,
        [parent]: { ...(prev[parent] || {}), [child]: value },
      }
    })
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  /**
   * Set a nested address.country_id from an AutocompleteCountries selection.
   */
  const setAddressCountry = (parentKey: string) => (selected: CountryValue) => {
    setField(`${parentKey}.country_id`, selected?.value ?? null)
  }

  /**
   * Build the value object for AutocompleteCountries from a nested
   * address path that stores country_id.
   */
  const getAddressCountryValue = (parentKey: string): CountryValue => ({
    label: '', // AutocompleteCountries resolves the label from its own Redux list by value
    value: formData[parentKey]?.country_id ?? '',
  })

  /**
   * Set a flat *_country_id field from an AutocompleteCountries selection.
   */
  const setFlatCountry = (fieldKey: string) => (selected: CountryValue) => {
    setField(fieldKey, selected?.value ?? null)
  }

  const getFlatCountryValue = (fieldKey: string): CountryValue => ({
    label: '',
    value: formData[fieldKey] ?? '',
  })

  /**
   * Client-side validation. Server-side errors override on submission.
   */
  const validateStep = (): boolean => {
    const errs: Record<string, string> = {}

    if (activeStep === 1) {
      if (selectedType === 'w9') {
        if (!formData.name?.trim()) errs.name = t('field_required')
        if (!formData.federal_tax_classification) errs.federal_tax_classification = t('field_required')
        if (!formData.address_line?.trim()) errs.address_line = t('field_required')
        if (!formData.city_state_zip?.trim()) errs.city_state_zip = t('field_required')
        if (!formData.ssn?.trim() && !formData.ein?.trim()) errs.ssn = t('ssn_or_ein_required')
      } else if (selectedType === 'w8ben') {
        if (!formData.individual_name?.trim()) errs.individual_name = t('field_required')
        if (!formData.country_of_citizenship_id) errs.country_of_citizenship_id = t('field_required')
        if (!formData.permanent_residence?.street?.trim()) errs['permanent_residence.street'] = t('field_required')
        if (!formData.permanent_residence?.city?.trim()) errs['permanent_residence.city'] = t('field_required')
        if (!formData.permanent_residence?.country_id) errs['permanent_residence.country_id'] = t('field_required')
        if (!formData.date_of_birth?.trim()) errs.date_of_birth = t('field_required')
      } else if (selectedType === 'w8bene') {
        if (!formData.organization_name?.trim()) errs.organization_name = t('field_required')
        if (!formData.country_of_incorporation_id) errs.country_of_incorporation_id = t('field_required')
        if (!formData.chapter_3_status) errs.chapter_3_status = t('field_required')
        if (!formData.chapter_4_status) errs.chapter_4_status = t('field_required')
        if (!formData.permanent_residence?.street?.trim()) errs['permanent_residence.street'] = t('field_required')
        if (!formData.permanent_residence?.city?.trim()) errs['permanent_residence.city'] = t('field_required')
        if (!formData.permanent_residence?.country_id) errs['permanent_residence.country_id'] = t('field_required')
      }
    }

    if (activeStep === 2) {
      if (selectedType === 'w8bene' && !formData.authorized_signer_name?.trim()) {
        errs.authorized_signer_name = t('field_required')
      }
      if (!formData.capacity_to_sign) errs.capacity_to_sign = t('must_accept_certification')
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleNext = () => {
    if (!validateStep()) return
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  /**
   * Submit the form to the type-specific API endpoint. Payload shape
   * matches the StoreXxxTaxFormRequest rules 1:1.
   */
  const handleSubmit = async () => {
    if (!validateStep()) return

    setSubmitting(true)
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })

    try {
      // Ensure mandatory boolean fields are explicitly present, since
      // the backend uses 'accepted' / 'declined' rules that fail when
      // the field is missing.
      const payload = {
        ...formData,
        is_us_person:     selectedType === 'w9',
        capacity_to_sign: Boolean(formData.capacity_to_sign),
        signature_date:   formData.signature_date || new Date().toISOString().split('T')[0],
      }

      const resp = await instance.post(
        `seller/tax-center/tax-forms/${selectedType}`,
        payload
      ) as typeResponse

      if ((resp as any).status === 'success') {
        onComplete()
      }
    } catch (error: any) {
      const serverErrors = error?.response?.data?.errors
      if (serverErrors) {
        const mapped: Record<string, string> = {}
        Object.entries(serverErrors).forEach(([key, messages]: [string, any]) => {
          mapped[key] = Array.isArray(messages) ? messages[0] : messages
        })
        setErrors(mapped)
        setActiveStep(1)
      }
    } finally {
      setSubmitting(false)
      Notiflix.Loading.remove()
    }
  }

  const getDeviationWarning = (): string => {
    if (selectedType === 'w9') return t('warning_w9_for_non_us_country')
    if (recommendedType === 'w9') return t('warning_w8_for_us_country')
    return t('warning_form_type_changed')
  }

  const handleSelectType = (typeCode: string) => {
    if (typeCode === selectedType) return
    setSelectedType(typeCode)
    setPrefillLoaded(false)
    setFormData({})
  }

  // ──────────────────────────────────────────────────────────────────
  //  Step 0: Select form type
  // ──────────────────────────────────────────────────────────────────

  const renderStepSelectType = () => (
    <div className={styles.stepContent}>
      <Typography className={styles.stepTitle}>
        {t('which_tax_form_do_you_need')}
      </Typography>
      <Typography className={styles.stepDescription}>
        {t('form_auto_detected_description')}
      </Typography>

      <div className={styles.typeOptionsGrid}>
        {availableTypes.map((type) => {
          const isSelected    = selectedType === type.code
          const isRecommended = type.code === recommendedType
          return (
            <div
              key={type.code}
              className={`${styles.typeOption} ${isSelected ? styles.typeOptionSelected : ''}`}
              onClick={() => handleSelectType(type.code)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSelectType(type.code)
                }
              }}
            >
              <FormControlLabel
                value={type.code}
                control={<CustomRadio checked={isSelected} />}
                label=""
                sx={{ m: 0, mr: 1 }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className={styles.typeOptionContent}>
                <Typography className={styles.typeLabel}>
                  {type.label}
                  {isRecommended && (
                    <span className={styles.recommendedBadge}>
                      {t('recommended_for_you')}
                    </span>
                  )}
                </Typography>
                <Typography className={styles.typeDescription}>
                  {type.description}
                </Typography>
              </div>
            </div>
          )
        })}
      </div>

      {isDeviatingFromRecommendation && (
        <Alert severity="warning" icon={<InfoOutlinedIcon />} className={styles.deviationAlert}>
          <Typography className={styles.deviationTitle}>
            {t('please_confirm_your_choice')}
          </Typography>
          <Typography className={styles.deviationText}>
            {getDeviationWarning()}
          </Typography>
        </Alert>
      )}
    </div>
  )

  // ──────────────────────────────────────────────────────────────────
  //  Step 1: W-9 fields (US persons only)
  // ──────────────────────────────────────────────────────────────────

  const renderFieldsW9 = () => (
    <>
      <div className={styles.fullWidth}>
        <TextField
          label={t('name_entity_individual')}
          value={formData.name ?? ''}
          onChange={(e) => setField('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth size="small" required
        />
      </div>

      <TextField
        label={t('business_name_dba')}
        value={formData.business_name ?? ''}
        onChange={(e) => setField('business_name', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('federal_tax_classification')}
        value={formData.federal_tax_classification ?? ''}
        onChange={(e) => setField('federal_tax_classification', e.target.value)}
        error={!!errors.federal_tax_classification}
        helperText={errors.federal_tax_classification}
        fullWidth size="small" select required
      >
        <MenuItem value="individual">{t('individual_sole_proprietor')}</MenuItem>
        <MenuItem value="sole_proprietor">{t('sole_proprietor_dba')}</MenuItem>
        <MenuItem value="c_corp">{t('c_corporation')}</MenuItem>
        <MenuItem value="s_corp">{t('s_corporation')}</MenuItem>
        <MenuItem value="partnership">{t('partnership')}</MenuItem>
        <MenuItem value="trust_estate">{t('trust_estate')}</MenuItem>
        <MenuItem value="llc">{t('llc')}</MenuItem>
        <MenuItem value="other">{t('other')}</MenuItem>
      </TextField>

      {formData.federal_tax_classification === 'llc' && (
        <TextField
          label={t('llc_tax_classification')}
          value={formData.llc_tax_classification ?? ''}
          onChange={(e) => setField('llc_tax_classification', e.target.value)}
          fullWidth size="small" select
        >
          <MenuItem value="C">C — C Corporation</MenuItem>
          <MenuItem value="S">S — S Corporation</MenuItem>
          <MenuItem value="P">P — Partnership</MenuItem>
        </TextField>
      )}

      {formData.federal_tax_classification === 'other' && (
        <TextField
          label={t('other_classification_text')}
          value={formData.other_classification_text ?? ''}
          onChange={(e) => setField('other_classification_text', e.target.value)}
          fullWidth size="small"
        />
      )}

      <div className={styles.fullWidth}>
        <TextField
          label={t('address_line')}
          value={formData.address_line ?? ''}
          onChange={(e) => setField('address_line', e.target.value)}
          error={!!errors.address_line}
          helperText={errors.address_line}
          fullWidth size="small" required
        />
      </div>

      <TextField
        label={t('city_state_zip')}
        value={formData.city_state_zip ?? ''}
        onChange={(e) => setField('city_state_zip', e.target.value)}
        error={!!errors.city_state_zip}
        helperText={errors.city_state_zip}
        fullWidth size="small" required
      />

      <TextField
        label={t('account_numbers')}
        value={formData.account_numbers ?? ''}
        onChange={(e) => setField('account_numbers', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('ssn_format')}
        value={formData.ssn ?? ''}
        onChange={(e) => setField('ssn', e.target.value)}
        error={!!errors.ssn}
        helperText={errors.ssn || t('ssn_or_ein_hint')}
        fullWidth size="small" placeholder="XXX-XX-XXXX"
      />

      <TextField
        label={t('ein_format')}
        value={formData.ein ?? ''}
        onChange={(e) => setField('ein', e.target.value)}
        error={!!errors.ein}
        helperText={errors.ein}
        fullWidth size="small" placeholder="XX-XXXXXXX"
      />
    </>
  )

  // ──────────────────────────────────────────────────────────────────
  //  Step 1: W-8BEN fields (non-US individuals)
  // ──────────────────────────────────────────────────────────────────

  const renderFieldsW8Ben = () => (
    <>
      <TextField
        label={t('name_of_individual')}
        value={formData.individual_name ?? ''}
        onChange={(e) => setField('individual_name', e.target.value)}
        error={!!errors.individual_name}
        helperText={errors.individual_name}
        fullWidth size="small" required
      />

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getFlatCountryValue('country_of_citizenship_id')}
          setSelectedCountry={setFlatCountry('country_of_citizenship_id')}
          className={styles.countryField}
          size="small"
          isRequired
          isError={!!errors.country_of_citizenship_id}
          errorText={errors.country_of_citizenship_id}
        />
      </div>

      <Typography className={styles.sectionLabel}>{t('permanent_residence_address')}</Typography>

      <div className={styles.fullWidth}>
        <TextField
          label={t('street_address')}
          value={formData.permanent_residence?.street ?? ''}
          onChange={(e) => setField('permanent_residence.street', e.target.value)}
          error={!!errors['permanent_residence.street']}
          helperText={errors['permanent_residence.street']}
          fullWidth size="small" required
        />
      </div>

      <TextField
        label={t('city')}
        value={formData.permanent_residence?.city ?? ''}
        onChange={(e) => setField('permanent_residence.city', e.target.value)}
        error={!!errors['permanent_residence.city']}
        helperText={errors['permanent_residence.city']}
        fullWidth size="small" required
      />

      <TextField
        label={t('state_province')}
        value={formData.permanent_residence?.state ?? ''}
        onChange={(e) => setField('permanent_residence.state', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('postal_code')}
        value={formData.permanent_residence?.postal_code ?? ''}
        onChange={(e) => setField('permanent_residence.postal_code', e.target.value)}
        fullWidth size="small"
      />

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getAddressCountryValue('permanent_residence')}
          setSelectedCountry={setAddressCountry('permanent_residence')}
          className={styles.countryField}
          size="small"
          isRequired
          isError={!!errors['permanent_residence.country_id']}
          errorText={errors['permanent_residence.country_id']}
        />
      </div>

      {/* Mailing address toggle */}
      <div className={styles.fullWidth}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.mailing_address_same_as_residence !== false}
              onChange={(e) =>
                setField('mailing_address_same_as_residence', e.target.checked)
              }
              sx={{ color: '#c11b1e', '&.Mui-checked': { color: '#c11b1e' } }}
            />
          }
          label={<Typography sx={{ fontSize: '0.85rem' }}>{t('mailing_same_as_residence')}</Typography>}
        />
      </div>

      {formData.mailing_address_same_as_residence === false && (
        <>
          <Typography className={styles.sectionLabel}>{t('mailing_address_optional')}</Typography>
          <div className={styles.fullWidth}>
            <TextField
              label={t('street_address')}
              value={formData.mailing_address?.street ?? ''}
              onChange={(e) => setField('mailing_address.street', e.target.value)}
              fullWidth size="small"
            />
          </div>
          <TextField
            label={t('city')}
            value={formData.mailing_address?.city ?? ''}
            onChange={(e) => setField('mailing_address.city', e.target.value)}
            fullWidth size="small"
          />
          <TextField
            label={t('postal_code')}
            value={formData.mailing_address?.postal_code ?? ''}
            onChange={(e) => setField('mailing_address.postal_code', e.target.value)}
            fullWidth size="small"
          />
          <div className={styles.autocompleteWrapper}>
            <AutocompleteCountries
              selectedCountry={getAddressCountryValue('mailing_address')}
              setSelectedCountry={setAddressCountry('mailing_address')}
              className={styles.countryField}
              size="small"
            />
          </div>
        </>
      )}

      <TextField
        label={t('foreign_tin')}
        value={formData.foreign_tin ?? ''}
        onChange={(e) => setField('foreign_tin', e.target.value)}
        fullWidth size="small"
        disabled={!!formData.ftin_not_legally_required}
      />

      <TextField
        label={t('date_of_birth')}
        value={formData.date_of_birth ?? ''}
        onChange={(e) => setField('date_of_birth', e.target.value)}
        error={!!errors.date_of_birth}
        helperText={errors.date_of_birth || 'YYYY-MM-DD'}
        fullWidth size="small"
        placeholder="1985-03-15"
        required
      />

      <div className={styles.fullWidth}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.ftin_not_legally_required}
              onChange={(e) => setField('ftin_not_legally_required', e.target.checked)}
              sx={{ color: '#c11b1e', '&.Mui-checked': { color: '#c11b1e' } }}
            />
          }
          label={<Typography sx={{ fontSize: '0.85rem' }}>{t('ftin_not_legally_required')}</Typography>}
        />
      </div>

      <Typography className={styles.sectionLabel}>{t('tax_treaty_benefits')}</Typography>

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getFlatCountryValue('treaty_country_id')}
          setSelectedCountry={setFlatCountry('treaty_country_id')}
          className={styles.countryField}
          size="small"
        />
      </div>

      <TextField
        label={t('treaty_rate_percent')}
        value={formData.treaty_rate ?? ''}
        onChange={(e) => setField('treaty_rate', e.target.value)}
        fullWidth size="small" placeholder="10"
      />

      <TextField
        label={t('treaty_article')}
        value={formData.treaty_article ?? ''}
        onChange={(e) => setField('treaty_article', e.target.value)}
        fullWidth size="small" placeholder="12"
      />

      <TextField
        label={t('treaty_paragraph')}
        value={formData.treaty_paragraph ?? ''}
        onChange={(e) => setField('treaty_paragraph', e.target.value)}
        fullWidth size="small" placeholder="2(a)"
      />

      <div className={styles.fullWidth}>
        <TextField
          label={t('type_of_income')}
          value={formData.treaty_income_type ?? ''}
          onChange={(e) => setField('treaty_income_type', e.target.value)}
          fullWidth size="small"
          placeholder={t('royalties_stock_photography')}
        />
      </div>

      <div className={styles.fullWidth}>
        <TextField
          label={t('conditions_explanation')}
          value={formData.treaty_conditions ?? ''}
          onChange={(e) => setField('treaty_conditions', e.target.value)}
          fullWidth size="small" multiline rows={2}
        />
      </div>
    </>
  )

  // ──────────────────────────────────────────────────────────────────
  //  Step 1: W-8BEN-E fields (non-US entities)
  // ──────────────────────────────────────────────────────────────────

  const renderFieldsW8BenE = () => (
    <>
      <TextField
        label={t('organization_name')}
        value={formData.organization_name ?? ''}
        onChange={(e) => setField('organization_name', e.target.value)}
        error={!!errors.organization_name}
        helperText={errors.organization_name}
        fullWidth size="small" required
      />

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getFlatCountryValue('country_of_incorporation_id')}
          setSelectedCountry={setFlatCountry('country_of_incorporation_id')}
          className={styles.countryField}
          size="small"
          isRequired
          isError={!!errors.country_of_incorporation_id}
          errorText={errors.country_of_incorporation_id}
        />
      </div>

      <div className={styles.fullWidth}>
        <TextField
          label={t('disregarded_entity_name')}
          value={formData.disregarded_entity_name ?? ''}
          onChange={(e) => setField('disregarded_entity_name', e.target.value)}
          helperText={t('disregarded_entity_name_hint')}
          fullWidth size="small"
        />
      </div>

      {/* Disregarded entity / branch address — fills Part II (Page 2 lines 12-13)
          on the IRS form. Shown only when the seller has named a disregarded
          entity above, since this section is optional and irrelevant otherwise. */}
      {!!formData.disregarded_entity_name?.trim() && (
        <>
          <Typography className={styles.sectionLabel}>
            {t('disregarded_entity_address')}
          </Typography>

          <div className={styles.fullWidth}>
            <TextField
              label={t('street_address')}
              value={formData.disregarded_entity_address?.street ?? ''}
              onChange={(e) => setField('disregarded_entity_address.street', e.target.value)}
              fullWidth size="small"
            />
          </div>

          <TextField
            label={t('city')}
            value={formData.disregarded_entity_address?.city ?? ''}
            onChange={(e) => setField('disregarded_entity_address.city', e.target.value)}
            fullWidth size="small"
          />

          <TextField
            label={t('postal_code')}
            value={formData.disregarded_entity_address?.postal_code ?? ''}
            onChange={(e) => setField('disregarded_entity_address.postal_code', e.target.value)}
            fullWidth size="small"
          />

          <div className={styles.autocompleteWrapper}>
            <AutocompleteCountries
              selectedCountry={getAddressCountryValue('disregarded_entity_address')}
              setSelectedCountry={setAddressCountry('disregarded_entity_address')}
              className={styles.countryField}
              size="small"
            />
          </div>

          <TextField
            label={t('disregarded_entity_giin')}
            value={formData.disregarded_entity_giin ?? ''}
            onChange={(e) => setField('disregarded_entity_giin', e.target.value)}
            fullWidth size="small"
          />
        </>
      )}

      <TextField
        label={t('chapter_3_status')}
        value={formData.chapter_3_status ?? ''}
        onChange={(e) => setField('chapter_3_status', e.target.value)}
        error={!!errors.chapter_3_status}
        helperText={errors.chapter_3_status}
        fullWidth size="small" select required
      >
        <MenuItem value="corporation">{t('corporation')}</MenuItem>
        <MenuItem value="partnership">{t('partnership')}</MenuItem>
        <MenuItem value="simple_trust">{t('simple_trust')}</MenuItem>
        <MenuItem value="complex_trust">{t('complex_trust')}</MenuItem>
        <MenuItem value="grantor_trust">{t('grantor_trust')}</MenuItem>
        <MenuItem value="estate">{t('estate')}</MenuItem>
        <MenuItem value="tax_exempt_organization">{t('tax_exempt_organization')}</MenuItem>
        <MenuItem value="private_foundation">{t('private_foundation')}</MenuItem>
        <MenuItem value="disregarded_entity">{t('disregarded_entity')}</MenuItem>
        <MenuItem value="central_bank">{t('central_bank')}</MenuItem>
        <MenuItem value="foreign_government_integral">{t('foreign_government_integral')}</MenuItem>
        <MenuItem value="foreign_government_controlled">{t('foreign_government_controlled')}</MenuItem>
        <MenuItem value="international_organization">{t('international_organization')}</MenuItem>
      </TextField>

      <TextField
        label={t('chapter_4_fatca_status')}
        value={formData.chapter_4_status ?? ''}
        onChange={(e) => setField('chapter_4_status', e.target.value)}
        error={!!errors.chapter_4_status}
        helperText={errors.chapter_4_status}
        fullWidth size="small" select required
      >
        <MenuItem value="active_nffe">{t('active_nffe')}</MenuItem>
        <MenuItem value="passive_nffe">{t('passive_nffe')}</MenuItem>
        <MenuItem value="publicly_traded_nffe">{t('publicly_traded_nffe')}</MenuItem>
        <MenuItem value="nonprofit_organization">{t('nonprofit_organization')}</MenuItem>
        <MenuItem value="section_501c_organization">{t('section_501c_organization')}</MenuItem>
        <MenuItem value="participating_ffi">{t('participating_ffi')}</MenuItem>
        <MenuItem value="reporting_model_1_ffi">{t('reporting_model_1_ffi')}</MenuItem>
        <MenuItem value="reporting_model_2_ffi">{t('reporting_model_2_ffi')}</MenuItem>
        <MenuItem value="nonreporting_iga_ffi">{t('nonreporting_iga_ffi')}</MenuItem>
        <MenuItem value="exempt_retirement_plan">{t('exempt_retirement_plan')}</MenuItem>
      </TextField>

      <Typography className={styles.sectionLabel}>{t('permanent_residence_address')}</Typography>

      <div className={styles.fullWidth}>
        <TextField
          label={t('street_address')}
          value={formData.permanent_residence?.street ?? ''}
          onChange={(e) => setField('permanent_residence.street', e.target.value)}
          error={!!errors['permanent_residence.street']}
          helperText={errors['permanent_residence.street']}
          fullWidth size="small" required
        />
      </div>

      <TextField
        label={t('city')}
        value={formData.permanent_residence?.city ?? ''}
        onChange={(e) => setField('permanent_residence.city', e.target.value)}
        error={!!errors['permanent_residence.city']}
        helperText={errors['permanent_residence.city']}
        fullWidth size="small" required
      />

      <TextField
        label={t('state_province')}
        value={formData.permanent_residence?.state ?? ''}
        onChange={(e) => setField('permanent_residence.state', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('postal_code')}
        value={formData.permanent_residence?.postal_code ?? ''}
        onChange={(e) => setField('permanent_residence.postal_code', e.target.value)}
        fullWidth size="small"
      />

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getAddressCountryValue('permanent_residence')}
          setSelectedCountry={setAddressCountry('permanent_residence')}
          className={styles.countryField}
          size="small"
          isRequired
          isError={!!errors['permanent_residence.country_id']}
          errorText={errors['permanent_residence.country_id']}
        />
      </div>

      <div className={styles.fullWidth}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.mailing_address_same_as_residence !== false}
              onChange={(e) => setField('mailing_address_same_as_residence', e.target.checked)}
              sx={{ color: '#c11b1e', '&.Mui-checked': { color: '#c11b1e' } }}
            />
          }
          label={<Typography sx={{ fontSize: '0.85rem' }}>{t('mailing_same_as_residence')}</Typography>}
        />
      </div>

      {formData.mailing_address_same_as_residence === false && (
        <>
          <Typography className={styles.sectionLabel}>{t('mailing_address_optional')}</Typography>
          <div className={styles.fullWidth}>
            <TextField
              label={t('street_address')}
              value={formData.mailing_address?.street ?? ''}
              onChange={(e) => setField('mailing_address.street', e.target.value)}
              fullWidth size="small"
            />
          </div>
          <TextField
            label={t('city')}
            value={formData.mailing_address?.city ?? ''}
            onChange={(e) => setField('mailing_address.city', e.target.value)}
            fullWidth size="small"
          />
          <TextField
            label={t('postal_code')}
            value={formData.mailing_address?.postal_code ?? ''}
            onChange={(e) => setField('mailing_address.postal_code', e.target.value)}
            fullWidth size="small"
          />
          <div className={styles.autocompleteWrapper}>
            <AutocompleteCountries
              selectedCountry={getAddressCountryValue('mailing_address')}
              setSelectedCountry={setAddressCountry('mailing_address')}
              className={styles.countryField}
              size="small"
            />
          </div>
        </>
      )}

      <TextField
        label={t('us_tin')}
        value={formData.us_tin ?? ''}
        onChange={(e) => setField('us_tin', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('giin')}
        value={formData.giin ?? ''}
        onChange={(e) => setField('giin', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('foreign_tin')}
        value={formData.foreign_tin ?? ''}
        onChange={(e) => setField('foreign_tin', e.target.value)}
        fullWidth size="small"
        disabled={!!formData.ftin_not_legally_required}
      />

      <TextField
        label={t('reference_number')}
        value={formData.reference_number ?? ''}
        onChange={(e) => setField('reference_number', e.target.value)}
        fullWidth size="small"
      />

      <div className={styles.fullWidth}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.ftin_not_legally_required}
              onChange={(e) => setField('ftin_not_legally_required', e.target.checked)}
              sx={{ color: '#c11b1e', '&.Mui-checked': { color: '#c11b1e' } }}
            />
          }
          label={<Typography sx={{ fontSize: '0.85rem' }}>{t('ftin_not_legally_required')}</Typography>}
        />
      </div>

      <Typography className={styles.sectionLabel}>{t('tax_treaty_benefits')}</Typography>

      <div className={styles.autocompleteWrapper}>
        <AutocompleteCountries
          selectedCountry={getFlatCountryValue('treaty_country_id')}
          setSelectedCountry={setFlatCountry('treaty_country_id')}
          className={styles.countryField}
          size="small"
        />
      </div>

      <TextField
        label={t('treaty_rate_percent')}
        value={formData.treaty_rate ?? ''}
        onChange={(e) => setField('treaty_rate', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('treaty_article')}
        value={formData.treaty_article ?? ''}
        onChange={(e) => setField('treaty_article', e.target.value)}
        fullWidth size="small"
      />

      <TextField
        label={t('treaty_paragraph')}
        value={formData.treaty_paragraph ?? ''}
        onChange={(e) => setField('treaty_paragraph', e.target.value)}
        fullWidth size="small"
      />

      <div className={styles.fullWidth}>
        <TextField
          label={t('lob_provision')}
          value={formData.treaty_lob_provision ?? ''}
          onChange={(e) => setField('treaty_lob_provision', e.target.value)}
          fullWidth size="small" select
        >
          <MenuItem value="">—</MenuItem>
          <MenuItem value="government">{t('lob_government')}</MenuItem>
          <MenuItem value="tax_exempt_pension">{t('lob_tax_exempt_pension')}</MenuItem>
          <MenuItem value="other_tax_exempt">{t('lob_other_tax_exempt')}</MenuItem>
          <MenuItem value="publicly_traded_corp">{t('lob_publicly_traded_corp')}</MenuItem>
          <MenuItem value="publicly_traded_corp_subsidiary">{t('lob_publicly_traded_corp_subsidiary')}</MenuItem>
          <MenuItem value="ownership_base_erosion">{t('lob_ownership_base_erosion')}</MenuItem>
          <MenuItem value="derivative_benefits">{t('lob_derivative_benefits')}</MenuItem>
          <MenuItem value="active_trade_business">{t('lob_active_trade_business')}</MenuItem>
          <MenuItem value="favorable_discretionary">{t('lob_favorable_discretionary')}</MenuItem>
          <MenuItem value="no_lob_article">{t('lob_no_lob_article')}</MenuItem>
          <MenuItem value="other">{t('other')}</MenuItem>
        </TextField>
      </div>

      {formData.treaty_lob_provision === 'other' && (
        <div className={styles.fullWidth}>
          <TextField
            label={t('lob_other_article_text')}
            value={formData.treaty_lob_other_text ?? ''}
            onChange={(e) => setField('treaty_lob_other_text', e.target.value)}
            fullWidth size="small"
          />
        </div>
      )}

      <div className={styles.fullWidth}>
        <TextField
          label={t('type_of_income')}
          value={formData.treaty_income_type ?? ''}
          onChange={(e) => setField('treaty_income_type', e.target.value)}
          fullWidth size="small"
        />
      </div>

      <div className={styles.fullWidth}>
        <TextField
          label={t('conditions_explanation')}
          value={formData.treaty_conditions ?? ''}
          onChange={(e) => setField('treaty_conditions', e.target.value)}
          fullWidth size="small" multiline rows={2}
        />
      </div>
    </>
  )

  const renderStepFields = () => {
    if (!prefillLoaded) {
      return (
        <div className={styles.loadingStep}>
          <CircularProgress size={28} sx={{ color: '#c11b1e' }} />
          <Typography className={styles.loadingText}>{t('loading_form_data')}</Typography>
        </div>
      )
    }

    return (
      <div className={styles.stepContent}>
        <Typography className={styles.stepTitle}>
          {availableTypes.find((tp) => tp.code === selectedType)?.label ?? selectedType}
        </Typography>
        <div className={styles.fieldsGrid}>
          {selectedType === 'w9' && renderFieldsW9()}
          {selectedType === 'w8ben' && renderFieldsW8Ben()}
          {selectedType === 'w8bene' && renderFieldsW8BenE()}
        </div>
      </div>
    )
  }

  // ──────────────────────────────────────────────────────────────────
  //  Step 2: Review & sign
  // ──────────────────────────────────────────────────────────────────

  const renderStepReview = () => (
    <div className={styles.stepContent}>
      <Typography className={styles.stepTitle}>{t('review_and_sign')}</Typography>
      <Typography className={styles.stepDescription}>
        {t('review_certification_text')}
      </Typography>

      <div className={styles.reviewSignContent}>
        <div className={styles.certificationBox}>
          <Typography className={styles.certificationText}>
            {selectedType === 'w9'
              ? t('w9_certification_text')
              : selectedType === 'w8ben'
                ? t('w8ben_certification_text')
                : t('w8bene_certification_text')}
          </Typography>
        </div>

        {/* W-8BEN-E requires a separate "authorized signer name" field;
            for W-9 and W-8BEN the signer is implicitly the individual/entity. */}
        {selectedType === 'w8bene' && (
          <TextField
            label={t('print_name_of_authorized_signer')}
            value={formData.authorized_signer_name ?? ''}
            onChange={(e) => setField('authorized_signer_name', e.target.value)}
            error={!!errors.authorized_signer_name}
            helperText={errors.authorized_signer_name}
            fullWidth size="small" required
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.capacity_to_sign}
              onChange={(e) => setField('capacity_to_sign', e.target.checked)}
              sx={{ color: '#c11b1e', '&.Mui-checked': { color: '#c11b1e' } }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.85rem' }}>
              {t('i_certify_under_penalties_of_perjury')}
            </Typography>
          }
        />
        {errors.capacity_to_sign && (
          <Typography sx={{ fontSize: '0.75rem', color: '#c11b1e', ml: 4 }}>
            {errors.capacity_to_sign}
          </Typography>
        )}
      </div>
    </div>
  )

  return (
    <div className={styles.wizardContainer}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onCancel}
        className={styles.backLink}
      >
        {t('back_to_tax_center')}
      </Button>

      <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  '&.Mui-active':    { color: '#c11b1e' },
                  '&.Mui-completed': { color: '#2e7d32' },
                },
              }}
            >
              {t(label)}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && renderStepSelectType()}
      {activeStep === 1 && renderStepFields()}
      {activeStep === 2 && renderStepReview()}

      <div className={styles.navButtons}>
        {activeStep > 0 && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            className={styles.navBack}
            disabled={submitting}
          >
            {t('back')}
          </Button>
        )}
        <div className={styles.navSpacer} />
        {activeStep < STEP_LABELS.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            className={styles.navNext}
          >
            {t('continue')}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={
              submitting
                ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                : <SendIcon />
            }
            onClick={handleSubmit}
            className={styles.navSubmit}
            disabled={submitting}
          >
            {t('submit_tax_form')}
          </Button>
        )}
      </div>
    </div>
  )
}